import React, { useState, useEffect } from "react";
import {
  Loader2,
  AlertCircle,
  IndianRupee,
  Settings,
  Globe,
  PackageSearch,
  Boxes,
  BadgeCheck,
  Link2Icon,
  BoomBox,
} from "lucide-react";

import { fetchAssignedServiceData } from "../../services/customerService";

const AssignedReport = () => {
  const [filters, setFilters] = useState({
    total_data: 90,
    user_id: "",
    service_id: "",
    package_id: "",
  });

  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [userData, setUserData] = useState({});

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const apiParams = {
        total_data: filters.total_data,
        ...(filters.user_id && { user_id: filters.user_id.trim() }),
        ...(filters.service_id && { service_id: parseInt(filters.service_id) }),
        ...(filters.package_id && { package_id: parseInt(filters.package_id) }),
      };

      const response = await fetchAssignedServiceData(apiParams);

      if (response.error) {
        setError(response.error);
        setReportData([]);
      } else {
        const uData = response.user || {};
        setUserData(uData);
        const data = response.data || response;

        const cleaned = Array.isArray(data) ? data : [];
        setReportData(cleaned);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch data");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasInitialLoad) {
      fetchData();
      setHasInitialLoad(true);
    }
  }, [hasInitialLoad]);

  useEffect(() => {
    applyLocalFilters();
  }, [reportData]);

  const applyLocalFilters = () => {
    setFilteredData([...reportData]);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  return (
    <div className="p-4 mt-6">
      <div className="bg-white p-2 rounded-md shadow mb-2">
        <h3 className="text-lg font-semibold mb-3 text-center">
          Assigned Service Reports
        </h3>
        <div className="flex items-end gap-6 justify-center">
          {/* User ID Field */}
          <div className="w-1/4">
            <label className="block text-md font-bold text-gray-700">
              User ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={filters.user_id}
              onChange={(e) => handleFilterChange("user_id", e.target.value)}
              placeholder="APID0326"
              className="w-full border px-2 py-2 rounded-md border-blue-600 uppercase"
            />
          </div>
        </div>
        {/* User Info */}
        <div className="flex justify-center mt-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 shadow-sm w-full max-w-3xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-800">
              <p>
                <span className="font-semibold text-gray-700">Name:</span>{" "}
                {userData?.name || "-"}
              </p>

              <p>
                <span className="font-semibold text-gray-700">
                  Authorized Sign Name:
                </span>{" "}
                {userData?.company_details?.authorized_person_name || "-"}
              </p>

              <p>
                <span className="font-semibold text-gray-700">Created At:</span>{" "}
                {userData?.created_at || "-"}
              </p>

              <p>
                <span className="font-semibold text-gray-700">
                  Company Name:
                </span>{" "}
                {userData?.company_details?.company_name || "-"}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-center">
          <button
            onClick={handleApplyFilters}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-2 py-1 rounded-md"
          >
            Search
          </button>
        </div>
      </div>

      <div className="flex justify-center">
        <h2 className="text-xl font-bold mb-2 text-center text-white bg-gradient-to-r from-blue-500 to-purple-500 py-1 px-2 rounded shadow">
          Assigned Report
        </h2>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 text-blue-500 text-lg font-semibold">
          <Loader2 className="animate-spin" /> Loading...
        </div>
      ) : error ? (
        <div className="text-red-600 flex items-center justify-center gap-2 font-semibold">
          <AlertCircle /> {error}
        </div>
      ) : filteredData.length === 0 ? (
        <div className="text-gray-500 text-center font-semibold">
          No data found.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-2 p-4">
          {filteredData.map((item, idx) => (
            <div
              key={idx}
              className="group relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 flex flex-col overflow-hidden"
            >
              {/* Top Hover Accent Line */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              {/* Status Badge */}
              <span
                className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase ${
                  item.assignment.status?.value === "active"
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {item.assignment.status?.value || "N/A"}
              </span>
              {/* Card Header (Service Info & Status) */}
              <div className="p-5 pb-4">
                <div className="flex justify-between items-start gap-4">
                  {/* Added flex-1 and min-w-0 to the left section so it shrinks instead of pushing the badge out */}
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="p-2.5 bg-indigo-50 rounded-xl text-indigo-600 shrink-0">
                      <PackageSearch className="w-5 h-5" />
                    </div>

                    {/* Added flex-1 and min-w-0 to text container */}
                    <div className="flex-1 min-w-0">
                      {/* Added break-words to wrap long service names naturally */}
                      <h3 className="font-bold text-gray-900 text-base leading-tight break-words">
                        {item?.service?.service_name || "Unknown Service"}
                      </h3>

                      {/* Changed items-center to items-start so icon stays at top if text wraps */}
                      <div className="flex items-start gap-1.5 text-xs text-gray-500 mt-1.5">
                        {/* Added shrink-0 and a top margin to align the icon with wrapped text */}
                        <Link2Icon className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        {/* Added break-all to ensure long endpoint URLs wrap properly without overflowing */}
                        <span className="break-all">
                          {item?.service?.end_point || "No endpoint"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-100 to-transparent"></div>

              {/* Card Body (Secondary Details Grid) */}
              {/* Changed to grid-cols-1 sm:grid-cols-2 to prevent crowding on very small screens */}
              <div className="p-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-4 text-sm grow">
                <div>
                  <p className="text-gray-400 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1 mb-1">
                    <Globe className="w-3.5 h-3.5 text-green-500 shrink-0" />{" "}
                    Environment
                  </p>
                  <p className="font-semibold text-gray-800 break-words">
                    {item?.assignment.environment || "N/A"}
                  </p>
                </div>

                <div>
                  <p className="text-gray-400 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1 mb-1">
                    <Boxes className="w-3.5 h-3.5 text-yellow-500 shrink-0" />{" "}
                    Package
                  </p>
                  {/* Replaced line-clamp-1 with break-words so data isn't hidden on small screens */}
                  <p className="font-semibold text-gray-800 break-words">
                    {item?.assignment.package?.name || "N/A"}
                  </p>
                </div>

                <div>
                  <p className="text-gray-400 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1 mb-1">
                    <Settings className="w-3.5 h-3.5 text-purple-500 shrink-0" />{" "}
                    Charge Type
                  </p>
                  <p className="font-semibold text-gray-800 break-words">
                    {item?.assignment.charges_commission?.service_charge
                      ?.charge_type || "N/A"}
                  </p>
                </div>

                <div>
                  <p className="text-gray-400 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1 mb-1">
                    <PackageSearch className="w-3.5 h-3.5 text-blue-500 shrink-0" />{" "}
                    Scope
                  </p>
                  {/* Replaced line-clamp-1 with break-words */}
                  <p className="font-semibold text-gray-800 break-words">
                    {item?.assignment.scope || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1 mb-1">
                    <Boxes className="w-3.5 h-3.5 text-blue-500 shrink-0" />{" "}
                    Monthly Volume
                  </p>
                  {/* Replaced line-clamp-1 with break-words */}
                  <p className="font-semibold text-gray-800 break-words">
                    {item?.assignment?.volume_count || "N/A"}
                  </p>
                </div>
              </div>

              {/* Card Footer (Price Section) */}
              <div className="p-5 py-3.5 bg-gray-50 mt-auto border-t border-gray-100 flex items-center justify-between group-hover:bg-indigo-50/50 transition-colors duration-300">
                <span className="text-gray-500 text-sm font-medium">
                  Service Charge
                </span>
                <div className="flex items-center text-gray-900 font-black text-lg">
                  <IndianRupee className="w-4 h-4 text-gray-500 mr-0.5" />
                  {item?.assignment.charges_commission?.service_charge
                    ?.amount || "N/A"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssignedReport;
